import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  ListItem,
  Radio,
  RadioGroup,
  Stack,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import * as Sentry from "@sentry/browser";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaGoogle, FaMicrosoft } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";

import {
  Alert,
  Button,
  Link,
  LoadingIndicator,
  SettingsPageContainer,
  StatusWithText,
  useToast,
} from "../../../components";
import {
  EventImportKeyword,
  GoogleAuthButton,
  MsftAuthButton,
} from "../../components";
import {
  CalendarHealthStatus,
  CalendarType,
  useCurrentUserCalendarQuery,
  useRevokeUserCalendarsMutation,
  useUpdateCalendarSettingsMutation,
} from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import useOAuth from "../../hooks/useOAuth";

interface CalendarStatusProps {
  calendarType: CalendarType;
  healthStatus: CalendarHealthStatus | null | undefined;
  isRevokeLoading: boolean;
  isConnectLoading: boolean;
  onReconnect: (calendarType: CalendarType) => void;
  onRevoke: () => void;
}

const CalendarStatus: React.FC<CalendarStatusProps> = ({
  calendarType,
  healthStatus,
  isRevokeLoading,
  isConnectLoading,
  onReconnect,
  onRevoke,
}: CalendarStatusProps) => {
  const isRevokeCalendarEnabled = true;
  const calendarLabel = `${calendarType[0]}${calendarType
    .slice(1)
    .toLowerCase()} calendar`;
  return (
    <>
      {!!healthStatus && (
        <HStack
          spacing={4}
          mb={6}
          alignItems="center"
          justify="space-between"
          width="100%"
        >
          <StatusWithText
            mb={0}
            label={calendarLabel}
            passed={healthStatus === CalendarHealthStatus.Valid}
            warn={healthStatus === CalendarHealthStatus.Invalid}
            tooltipLabel={
              healthStatus === CalendarHealthStatus.Invalid
                ? "There is an issue with this connection. Please reconnect the calendar."
                : undefined
            }
          />
          {isRevokeCalendarEnabled && healthStatus && (
            <HStack spacing={4}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onReconnect(calendarType)}
                isLoading={isConnectLoading}
              >
                Reconnect calendar
              </Button>
              <Button
                variant="dangerOutline"
                size="sm"
                isLoading={isRevokeLoading}
                onClick={onRevoke}
              >
                Revoke calendar access
              </Button>
            </HStack>
          )}
        </HStack>
      )}
    </>
  );
};

const CalendarSettings: React.FC = () => {
  const toast = useToast();
  const [isPolling, setIsPolling] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const msftAuth = searchParams.get("msft_auth");
  const { data, loading, stopPolling, refetch } = useCurrentUserCalendarQuery();
  const sharedCalendarsEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "msft_shared_calendars"
  );
  const currentUser = data?.currentUser;
  const zoomSetupComplete =
    currentUser?.organization?.isZoomAuthorized ||
    currentUser?.organization?.zoomNotetakerEnabled ||
    false;
  const googleMeetEnabled =
    currentUser?.organization?.googleMeetEnabled || false;
  const videoMeetingEnabled = zoomSetupComplete || googleMeetEnabled;
  const calendars = currentUser?.calendars || [];

  const isRevokeCalendarEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "revoke_calendar_access"
  );

  const { onClick: onGoogleAuth, isLoading: isGoogleAuthLoading } = useOAuth({
    provider: "google",
    onAuth: () => refetch(),
    onError: ({ message }) => {
      toast({
        title: "Error",
        description: message,
        status: "error",
      });
    },
    feature: "calendar",
  });
  const { onClick: onMsftAuth, isLoading: isMsftAuthLoading } = useOAuth({
    provider: "msft",
    onAuth: () => refetch(),
    onError: ({ message, code, extra }) => {
      Sentry.captureMessage("Error syncing microsoft calendar", {
        extra: {
          userId: currentUser?.id,
          errorMessage: message,
          errorCode: code,
          extra,
        },
      });
      toast({
        title: "Error",
        description: message,
        status: "error",
      });
    },
    feature: sharedCalendarsEnabled ? "shared_calendar" : "calendar",
  });

  const [revokeUserCalendars, { loading: revokeLoading }] =
    useRevokeUserCalendarsMutation({ onCompleted: () => refetch() });

  const handleReconnect = (calendarType: CalendarType): void => {
    if (calendarType === CalendarType.Google) {
      onGoogleAuth();
    } else if (calendarType === CalendarType.Microsoft) {
      onMsftAuth();
    }
  };

  const successToast = (): void => {
    toast({
      status: "success",
      title: "Integrations",
      description:
        "Your Calendar events will appear on the home screen momentarily.",
    });
  };

  useEffect(() => {
    if (msftAuth) {
      if (msftAuth === "success") {
        successToast();
      } else if (msftAuth === "failure") {
        toast({
          status: "error",
          title: "Integrations",
          description: "There was a problem authorizing calendar access",
        });
      }
      searchParams.delete("msft_auth");
      setSearchParams(searchParams, { replace: true });
    }
  });

  const primaryCalendar = calendars.find((cal) => {
    return cal.primary;
  });

  useEffect(() => {
    stopPolling();
    setIsPolling(false);
  }, [primaryCalendar]);

  const [
    updateCalendarSettings,
    {
      error: updateCalendarSettingsError,
      loading: updateCalendarSettingsLoading,
    },
  ] = useUpdateCalendarSettingsMutation({
    onCompleted: (data) => {
      if (data?.updateCalendarSettings?.currentUser) {
        toast({
          status: "success",
          title: "Update Calendar Settings",
          description: "Success!",
        });
      }
    },
    onError: () => {
      toast({
        status: "error",
        title: "Update Calendar Settings",
        description: "There was a problem - please try again",
      });
    },
  });
  const { register, handleSubmit } = useForm<{
    videoMeetingImportStrategy: string;
  }>({
    values: {
      videoMeetingImportStrategy:
        primaryCalendar?.videoMeetingImportStrategy || "NONE",
    },
  });
  const submitCalendarSettings = handleSubmit((values) => {
    if (!primaryCalendar) return;
    updateCalendarSettings({
      variables: {
        id: primaryCalendar.id,
        shouldImportVideoMeeting: values.videoMeetingImportStrategy,
      },
    });
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  if (
    // TODO: remove "isCalendarAuthorized" when FF is removed.
    currentUser?.isCalendarAuthorized ||
    currentUser?.googleCalendarHealth ||
    currentUser?.msftCalendarHealth
  ) {
    return (
      <SettingsPageContainer
        heading="Calendar sync"
        subHeading="Sync your calendar to ensure BrightHire seamlessly joins your interviews."
      >
        <Box mb={6}>
          <CalendarStatus
            calendarType={CalendarType.Google}
            // TODO: keep "googleCalendarHealth" only once FF is removed.
            healthStatus={
              isRevokeCalendarEnabled
                ? currentUser.googleCalendarHealth
                : currentUser.isGcalAuthorized
                ? CalendarHealthStatus.Valid
                : null
            }
            isRevokeLoading={revokeLoading}
            isConnectLoading={isGoogleAuthLoading}
            onReconnect={handleReconnect}
            onRevoke={() =>
              revokeUserCalendars({
                variables: {
                  calendarType: CalendarType.Google,
                },
              })
            }
          />
          <CalendarStatus
            calendarType={CalendarType.Microsoft}
            healthStatus={
              isRevokeCalendarEnabled
                ? currentUser.msftCalendarHealth
                : currentUser.isMsftCalAuthorized
                ? CalendarHealthStatus.Valid
                : null
            }
            isRevokeLoading={revokeLoading}
            isConnectLoading={isMsftAuthLoading}
            onReconnect={handleReconnect}
            onRevoke={() =>
              revokeUserCalendars({
                variables: {
                  calendarType: CalendarType.Microsoft,
                },
              })
            }
          />
          {((isRevokeCalendarEnabled && !currentUser.googleCalendarHealth) ||
            !currentUser.isGcalAuthorized) && (
            <Flex w="96">
              <Button
                leftIcon={<FaGoogle />}
                width="100%"
                onClick={onGoogleAuth}
                isLoading={isGoogleAuthLoading}
                data-testid={isGoogleAuthLoading ? null : "google-auth-button"}
              >
                Sync your Google calendar
              </Button>
            </Flex>
          )}
          {((isRevokeCalendarEnabled && !currentUser.msftCalendarHealth) ||
            !currentUser.isMsftCalAuthorized) && (
            <Flex w="96">
              <Button
                leftIcon={<FaMicrosoft />}
                width="100%"
                onClick={onMsftAuth}
                isLoading={isMsftAuthLoading}
                data-testid={isMsftAuthLoading ? null : "msft-auth-button"}
              >
                Sync your Microsoft calendar
              </Button>
            </Flex>
          )}
          <Heading as="h2" fontSize="md" fontWeight="semibold" mt={6} mb={4}>
            My Calendars
          </Heading>
          <UnorderedList>
            {calendars?.map((calendar) => {
              return (
                <ListItem
                  fontWeight={calendar.primary ? "semibold" : "normal"}
                  key={calendar.id}
                  mb="2"
                  lineHeight="5"
                  _last={{ mb: "0" }}
                >
                  {calendar.name}
                </ListItem>
              );
            })}
          </UnorderedList>
        </Box>
        {isPolling && <LoadingIndicator />}
        {primaryCalendar && (
          <Flex mt="8" mb="8">
            {updateCalendarSettingsError && (
              <Alert
                status="error"
                title="Error"
                description={updateCalendarSettingsError.message}
              />
            )}
            <form onSubmit={submitCalendarSettings}>
              <Heading as="h2" fontSize="md" fontWeight="semibold" mb={4}>
                Video Meeting Import Settings
              </Heading>
              <StatusWithText
                passed={zoomSetupComplete}
                label={
                  zoomSetupComplete
                    ? "Zoom Setup Complete"
                    : "Zoom Setup Incomplete, please contact your Administrator."
                }
              />
              <StatusWithText
                passed={googleMeetEnabled}
                label={
                  googleMeetEnabled
                    ? "Google Meet Enabled"
                    : "Google Meet Not Enabled, please contact your Administrator."
                }
              />
              {videoMeetingEnabled && (
                <>
                  <FormControl id="videoMeetingImportStrategy" mb={6}>
                    <FormLabel>
                      {!primaryCalendar.canSetVideoMeetingImportStrategy
                        ? "An organization administrator has set this up for you. Please contact them to change this setting."
                        : "Use the settings below to have BrightHire automatically join and record Zoom or Google Meet interviews scheduled on your calendar. "}
                    </FormLabel>
                    <RadioGroup
                      name="videoMeetingImportStrategy"
                      defaultValue={primaryCalendar.videoMeetingImportStrategy}
                      mb={4}
                    >
                      <Stack spacing="4">
                        <Radio
                          {...register("videoMeetingImportStrategy")}
                          value="TAGGED"
                          isDisabled={
                            !primaryCalendar.canSetVideoMeetingImportStrategy
                          }
                        >
                          <b>Some</b>: Meetings with #brighthire in the event
                          description
                        </Radio>
                        <Radio
                          {...register("videoMeetingImportStrategy")}
                          value="NONE"
                          isDisabled={
                            !primaryCalendar.canSetVideoMeetingImportStrategy
                          }
                        >
                          <b>None</b>: You will need to{" "}
                          <Link href="https://help.brighthire.ai/en/articles/4439267-zoom-recording-meetings">
                            manually add BrightHire
                          </Link>
                          .
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                  <Button
                    type="submit"
                    mb={2}
                    isLoading={updateCalendarSettingsLoading}
                    isDisabled={
                      !primaryCalendar.canSetVideoMeetingImportStrategy
                    }
                  >
                    Save
                  </Button>
                </>
              )}
            </form>
          </Flex>
        )}
        <Divider my="5" />
        <EventImportKeyword w={650} h={350} />
      </SettingsPageContainer>
    );
  }

  return (
    <SettingsPageContainer heading="Calendar sync">
      <Flex flexDirection="column" alignItems="flex-start" w="auto">
        <Text mb="4">You currently have no synced calendars.</Text>
        <Flex w="128">
          <GoogleAuthButton
            label="Sync your Google calendar"
            onError={({ message }) => {
              toast({
                title: "Error",
                description: message,
                status: "error",
              });
            }}
            onAuth={() => {
              refetch();
            }}
            feature="calendar"
          />
        </Flex>
        <Flex w="128" mt={2}>
          <MsftAuthButton
            label="Sync your Microsoft calendar"
            onError={({ message, code, extra }) => {
              Sentry.captureMessage("Error syncing microsoft calendar", {
                extra: {
                  userId: currentUser?.id,
                  errorMessage: message,
                  errorCode: code,
                  extra,
                },
              });
              toast({
                title: "Error",
                description: message,
                status: "error",
              });
            }}
            onAuth={() => {
              refetch();
            }}
            feature="calendar"
          />
        </Flex>
      </Flex>
    </SettingsPageContainer>
  );
};

export default CalendarSettings;
